import { AppHistory, IHashParams, LocationListener } from 'app/containers/AppHistory';
import { AppActionCreator, AppActions } from 'app/redux/App/actions'
import { IAppState, store } from 'app/redux/App/store'
import { NavActionCreator, NavActions } from 'app/redux/Nav/actions'
import { ProfileActionCreator, ProfileActions } from 'app/redux/Profile/actions'
import { Profile } from 'app/redux/Profile/model'
import { UserActionCreator, UserActions } from 'app/redux/User/actions'
import * as utils from 'app/utils'
import { getBrandHelpers, getBrandInfo, getGATrackingID, getPipelineConfig, getRegistationInputs } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { parseFormattedNumber } from '../utils';

interface IContainerActions {
  readonly Nav: NavActionCreator
  readonly Profile: ProfileActionCreator
  readonly User: UserActionCreator
  readonly App: AppActionCreator
}

type snackbarStyles = 'primary' | 'success' | 'info' | 'warning' | 'danger'

const log = utils.makeDebug(false)

/**
 * Container = smart component
 * 1) A Container has access to the redux store via onAppStateUpdate method
 * 2) A Container has access to the App's Location/history via onLocationUpdate() method
 *
 * Both of these methods should/can be used to update [dumb] components contained inside of Containers
 */
abstract class Container<P, S> extends React.Component<P, S> {
  static lastLocationURL: string
  private static isInited = false

  abstract state: S

  protected debounceStoreProfile = _.debounce((p: Profile) => this.Actions.Profile.store(p), 500)

  protected readonly Actions: IContainerActions = {
    Nav: NavActions,
    Profile: ProfileActions,
    User: UserActions,
    App: AppActions,
  }

  private isUnmounting = false
  private removeAppStateListenerFN: () => void
  private removeHistoryListenerFn: () => void
  private snackbarTimeout: number
  private snackbar: HTMLElement
  private defaultScrollOptions: ScrollIntoViewOptions = { block: 'start', behavior: 'smooth', inline: 'nearest' }

  /**
   * Remove AppState and History listeners when component unmounts
   */
  componentWillUnmount() {
    this.isUnmounting = true
    this.removeAppStateListener()
    this.removeHistoryListener()
    this.hideSnackbar()
  }

  /**
   * Shows pop toast at bottom of screen
   * @param message
   * @param bgColor
   */
  protected showSnackbar(message: string, bgColor?: snackbarStyles, faIcon?: string) {
    clearTimeout(this.snackbarTimeout)

    if (this.isUnmounting) { return; }

    if (!this.snackbar) {
      this.snackbar = document.getElementById('snackbar')
    }

    this.snackbar.className = 'show '

    this.snackbar.innerHTML = message

    if (bgColor) {
      this.snackbar.className += bgColor
    }

    if (faIcon) {
      this.snackbar.innerHTML = `<i class="${faIcon}"></i> ${message}`
    }

    this.snackbarTimeout = window.setTimeout(() => this.hideSnackbar(), 3000)
  }

  /**
   * Remove Snackbar from screen
   */
  protected hideSnackbar() {
    clearTimeout(this.snackbarTimeout)

    if (this.snackbar) {
      this.snackbar.className = ''
    }
  }

  /**
   * Scroll an element into view via id (no hash needed)
   * @param id
   * @param options
   */
  protected scrollTo(id: string, options: ScrollIntoViewOptions = this.defaultScrollOptions) {
    //remove ancher
    if (id.startsWith('#')) {
      id = id.substring(1)
    }

    const element = document.getElementById(id)

    if (element) {
      element.scrollIntoView(options)
    }
  }

  /**
   * Utility Method to convert MaterialInput#onChange
   *
   * It allows for the input to contain garbage ($ and , for instance) and
   * simply removes anything not a digit.
   *
   * @param num
   */
  protected toNumber(num: string): number {
    return utils.toNumber(num)
  }

  /**
   * Convert number in string format to moment duration
   * @param num
   */
  protected numberStrToYearDuration(years: string) {
    const valueNum = this.toNumber(years)

    if (valueNum && !isNaN(valueNum)) {
      return moment.duration(valueNum, 'years')
    }

    return null
  }

  /**
   * Replace current location (removed from history)
   * @param path
   */
  protected replace(path: string) {
    return AppHistory.replace(path)
  }
  /**
   * relocate to new path
   * @param path
   * @param hashparams
   */
  protected relocate(path: string, hashparams?: IHashParams) {
    return AppHistory.relocate(path, hashparams)
  }

  /**
   * Get specific hash param
   * @param paramName
   */
  protected getHashParamValue(paramName: string) {
    const hashParams = this.getHashParams()
    const value = hashParams[paramName]

    if (value) {
      return value
    }

    return null
  }

  /**
   * Get existence of param
   * @param paramName
   */
  protected hasHashParam(paramName: string) {
    const hashParams = this.getHashParams()

    return paramName in hashParams
  }
  /**
   * get hash params such as #/profile?asdf=2
   *
   * return {asdf: 2}
   */
  protected getHashParams() {
    return AppHistory.getHashParams()
  }

  /**
   * Get current customer contact information
   */
  protected getCustomerBrandInfo() {
    return getBrandInfo()
  }

  protected getCustomerBrandHelpers() {
    return getBrandHelpers()
  }

  /**
   * Get current registration inputs config
   */
  protected getCustomerRegistationInputsConfig() {
    return getRegistationInputs()
  }

  /**
   * Get current pipeline config
   */
  protected getCustomerPipelineConfig() {
    return getPipelineConfig()
  }

  /**
   * Get current pipeline instances
   */
  protected getCustomerPipelineInstances() {
    const pipelineConfig = this.getCustomerPipelineConfig()
    const pipelineKeys = Object.keys(pipelineConfig)
    const pipelineInstances = pipelineKeys.map((key) => pipelineConfig[key])

    return pipelineInstances
  }

  /**
   * Subscribe to AppState updates
   * @param listener
   */
  protected onAppStateUpdate(listener: (AppState: IAppState) => void, immediate = true) {
    this.removeAppStateListener()

    const updateListener = () => {
      if (this.isUnmounting) { return }

      const state = store.getState()

      //catch errors since componentWillMount() will eat it
      try {
        listener(state)
      } catch (e) {
        // tslint:disable-next-line:no-console
        console.error(e)
      }
    }

    if (immediate) { updateListener() }

    this.removeAppStateListenerFN = store.subscribe(() => updateListener())
  }

  /**
   * Subscribe to URL location changes
   * @param listener
   * @param immediate
   */
  protected onLocationUpdate(listener: LocationListener, immediate?: boolean) {
    this.removeHistoryListener()

    this.removeHistoryListenerFn = AppHistory.listen((location) => {
      if (this.isUnmounting) { return }

      // if the location has really changed, tell GA about it
      const hash = (location.hash.startsWith('#/')) ? location.hash.substr(2) : location.hash
      const newLocationURL = location.pathname + hash
      if (Container.lastLocationURL !== newLocationURL) {
        log('onLocationUpdate to: ' + newLocationURL + ' (was: ' + Container.lastLocationURL + ')')
        Container.lastLocationURL = newLocationURL
        const w = window as any
        if (!Container.isInited) {
          Container.isInited = true
          w.gtag('js', new Date());
        }
        w.gtag('config', getGATrackingID(), {
          page_location: window.location.href,
          page_path: newLocationURL,
        })
      }
      listener(location)

    }, immediate)
  }

  /**
   * Remove listener from onAppStateUpdate()
   */
  protected removeAppStateListener() {
    if (typeof this.removeAppStateListenerFN === 'function') {
      this.removeAppStateListenerFN()
    }
  }

  /**
   * Remove listener from onHistoryUpdate()
   */
  protected removeHistoryListener() {
    if (typeof this.removeHistoryListenerFn === 'function') {
      this.removeHistoryListenerFn()
    }
  }

  /**
   * Get Redux store
   */
  protected getStore() {
    return store
  }

  /**
   * Get AppHistory object
   */
  protected getHistory() {
    return AppHistory
  }

  /**
   * Turns off the introduction page.
   *
   * @param profile - the users profile.
   */
  protected disableShowIntroduction(profile: Profile) {
    this.updateShowIntroduction(profile, false)
  }

  /**
   * Updates the user profile to mark whether or not, the introduction needs to be shown.
   *
   * @param profile - the user profile.
   * @param showIntroduction - truee, iff the introduction should be shown.
   */
  protected updateShowIntroduction(profile: Profile, showIntroduction: boolean) {
    if (profile.showIntroduction !== showIntroduction) {
      profile.showIntroduction = showIntroduction
      this.debounceStoreProfile(profile)
    }
  }

  /**
   * Updates the stored copy of a profile. Also clears the 'showIntroduction' flag.
   *
   * @param profile - the users profile.
   */
  protected markSignificantProfileUpdate(profile: Profile) {
    this.disableShowIntroduction(profile)
    this.debounceStoreProfile(profile)
  }
}

export {
  Container
}



// WEBPACK FOOTER //
// ./src/app/containers/Container.ts